import { ASSET_TYPE } from "@/features/campaigns/direct-mail/rrd/utils/rrdConstants";
import {
  getRRDSelectableAssetTypes,
  normalizeRRDPrintTypeContext,
} from "@/features/campaigns/direct-mail/rrd/utils/rrdLayoutHelper";

type RRDAssetPanelConfig = {
  key: "heroImages" | "largeHeroImages" | "mediumHeroImages" | "coupons";
  label: string;
  emptyType: string;
  tooltipType: string;
  countName: string;
};

type RRDAssetPanelSection = RRDAssetPanelConfig & {
  assetType: string;
  requiredCount?: number;
};

type RRDAssetPanelParams = {
  printType?: string;
  layoutKey?: string | null;
};

type RRDAssetInstructionParams = RRDAssetPanelParams & {
  isBulk?: boolean;
};

const ASSET_PANEL_CONFIG: Record<string, RRDAssetPanelConfig> = {
  [ASSET_TYPE.HERO]: {
    key: "heroImages",
    label: "Hero Images",
    emptyType: "hero images",
    tooltipType: "hero_images",
    countName: "hero image",
  },
  [ASSET_TYPE.LARGE_HERO]: {
    key: "largeHeroImages",
    label: "Large Hero Images",
    emptyType: "large hero images",
    tooltipType: "hero_images",
    countName: "large hero image",
  },
  [ASSET_TYPE.MEDIUM_HERO]: {
    key: "mediumHeroImages",
    label: "Medium Hero Images",
    emptyType: "medium hero images",
    tooltipType: "hero_images",
    countName: "medium hero image",
  },
  [ASSET_TYPE.COUPON]: {
    key: "coupons",
    label: "Coupons",
    emptyType: "coupons",
    tooltipType: "coupons",
    countName: "coupon",
  },
};

function pluralize(label: string, count: number) {
  return `${count} ${label}${count === 1 ? "" : "s"}`;
}

export function getRRDAssetPanelSections({
  printType,
  layoutKey,
}: RRDAssetPanelParams): RRDAssetPanelSection[] {
  const context = normalizeRRDPrintTypeContext({
    print_type: printType,
    layoutKey,
  });
  const selectableTypes = getRRDSelectableAssetTypes(context.layoutKey);
  return selectableTypes
    .map((assetType: string) => {
      const config = ASSET_PANEL_CONFIG[assetType];
      return {
        assetType,
        ...config,
        requiredCount:
          context.layoutSpec?.completionAssets?.[
            assetType === ASSET_TYPE.HERO
              ? "heroImages"
              : assetType === ASSET_TYPE.LARGE_HERO
                ? "largeHeroImages"
                : assetType === ASSET_TYPE.MEDIUM_HERO
                  ? "mediumHeroImages"
                  : "coupons"
          ],
      };
    })
    .filter((section: Partial<RRDAssetPanelSection>) => section.key);
}

export function getRRDAssetInstruction({
  printType,
  layoutKey,
  isBulk,
}: RRDAssetInstructionParams) {
  const sections = getRRDAssetPanelSections({ printType, layoutKey });
  const requiredParts = sections
    .filter((section) => section.requiredCount)
    .map((section) => pluralize(section.countName, section.requiredCount));

  if (!requiredParts.length) return "";

  const lastPart = requiredParts.pop();
  const requiredText = requiredParts.length
    ? `${requiredParts.join(", ")} and ${lastPart}`
    : lastPart;

  return `Select ${requiredText} to promote ${
    isBulk ? "selected locations" : "for this location"
  }`;
}
